import { z } from 'zod';
import {
  createNoteBodyContentMax,
  findLeadsResponseCustomerContactNameMax,
  findLeadsResponseCustomerContactNameMin,
  findLeadsResponseCustomerContactPhoneRegExp,
  findLeadsResponseCustomerIdMax,
  findLeadsResponseCustomerIdMin,
  findLeadsResponseCustomerNameMax,
  findLeadsResponseCustomerNameMin,
  findLeadsResponseHomeDealerCityMax,
  findLeadsResponseHomeDealerGroupNameMax,
  findLeadsResponseHomeDealerIdMax,
  findLeadsResponseHomeDealerIdMin,
  findLeadsResponseHomeDealerLocationCodeRegExp,
  findLeadsResponseHomeDealerNameMax,
  findLeadsResponseHomeDealerNameMin,
  findLeadsResponseHomeDealerStateRegExp,
  findLeadsResponseMaintenanceDueMileageValueMax,
  findLeadsResponseMaintenanceDueMileageValueMin,
  findLeadsResponseMaintenanceMaintenanceItemsItemDescriptionMax,
  findLeadsResponseMaintenanceMaintenanceItemsItemDescriptionMin,
  findLeadsResponseMaintenanceMaintenanceItemsItemPartsItemNumberMax,
  findLeadsResponseMaintenanceMaintenanceItemsItemPartsItemNumberMin,
  findLeadsResponseMaintenanceMaintenanceItemsItemPartsItemQuantityValueMax,
  findLeadsResponseMaintenanceMaintenanceItemsItemPartsItemQuantityValueMin,
  findLeadsResponseMaintenanceMaintenanceItemsItemPartsItemQuantityValueMultipleOf,
  findLeadsResponseMaintenanceMaintenanceItemsItemSrtsItemCodeMax,
  findLeadsResponseMaintenanceMaintenanceItemsItemSrtsItemCodeMin,
  findLeadsResponseMaintenanceMaintenanceItemsItemSrtsItemHoursMax,
  findLeadsResponseMaintenanceMaintenanceItemsItemSrtsItemHoursMin,
  findLeadsResponseMaintenanceMaintenanceItemsItemSrtsItemHoursMultipleOf,
  findLeadsResponseMaintenanceMaintenanceItemsItemVmrsCodeMax,
  findLeadsResponseMaintenanceMaintenanceItemsItemVmrsCodeMin,
  findLeadsResponseMaintenanceMaintenanceItemsItemVmrsCodeRegExp,
  findLeadsResponseVinMax,
  findLeadsResponseVinMin,
} from '@/api/generated/endpoints/leads/leads.zod.ts';
import {
  RFC3339InstantString,
  RFC3339PlainDateString,
  RFC3339ZonedDateTimeString,
} from '@/models/entities/openApiZodTemporal.ts';
import { extendWorkflow } from '@/models/entities/workflowExtensions';
import type { LeadStatusGroup } from '@/models/ui/leadStatusGroup.ts';

export const LeadId = z.string().uuid().brand<'LeadId'>();
export type LeadId = z.infer<typeof LeadId>;

export const LeadNoteIdBrand = z.string().uuid().brand<'LeadNoteId'>();
export type LeadNoteId = z.infer<typeof LeadNoteIdBrand>;

export const LeadNoteContentBrand = z
  .string()
  .min(1)
  .max(createNoteBodyContentMax)
  .brand<'LeadNoteContent'>();
export type LeadNoteContent = z.infer<typeof LeadNoteContentBrand>;

export const VinBrand = z
  .string()
  .min(findLeadsResponseVinMin)
  .max(findLeadsResponseVinMax)
  .brand<'Vin'>();
export type Vin = z.infer<typeof VinBrand>;

export const CustomerIdBrand = z
  .string()
  .min(findLeadsResponseCustomerIdMin)
  .max(findLeadsResponseCustomerIdMax)
  .brand<'CustomerId'>();
export type CustomerId = z.infer<typeof CustomerIdBrand>;

export const PhoneNumberBrand = z
  .string()
  .regex(findLeadsResponseCustomerContactPhoneRegExp)
  .brand<'PhoneNumber'>();
export type PhoneNumber = z.infer<typeof PhoneNumberBrand>;

export const DealerId = z
  .string()
  .min(findLeadsResponseHomeDealerIdMin)
  .max(findLeadsResponseHomeDealerIdMax)
  .brand<'DealerId'>();
export type DealerId = z.infer<typeof DealerId>;

export const SelectedDealerIdsSchema = z
  .array(DealerId)
  .nonempty()
  .max(10000)
  .transform((values) => new Set(values))
  .readonly();

export const AvailableDealerIdsSchema = z
  .array(DealerId)
  .nonempty()
  .max(10000)
  .transform((values) => new Set(values))
  .readonly();

export type SelectedDealerIds = z.infer<typeof SelectedDealerIdsSchema>;

export type AvailableDealerIds = z.infer<typeof AvailableDealerIdsSchema>;

export const DealerNameBrand = z
  .string()
  .min(findLeadsResponseHomeDealerNameMin)
  .max(findLeadsResponseHomeDealerNameMax)
  .brand<'DealerName'>();
export type DealerName = z.infer<typeof DealerNameBrand>;

export const VmrsCodeBrand = z
  .string()
  .min(findLeadsResponseMaintenanceMaintenanceItemsItemVmrsCodeMin)
  .max(findLeadsResponseMaintenanceMaintenanceItemsItemVmrsCodeMax)
  .regex(findLeadsResponseMaintenanceMaintenanceItemsItemVmrsCodeRegExp)
  .brand<'VmrsCode'>();
export type VmrsCode = z.infer<typeof VmrsCodeBrand>;

export const PartNumberBrand = z
  .string()
  .min(findLeadsResponseMaintenanceMaintenanceItemsItemPartsItemNumberMin)
  .max(findLeadsResponseMaintenanceMaintenanceItemsItemPartsItemNumberMax)
  .brand<'PartNumber'>();
export type PartNumber = z.infer<typeof PartNumberBrand>;

export const SrtCodeBrand = z
  .string()
  .min(findLeadsResponseMaintenanceMaintenanceItemsItemSrtsItemCodeMin)
  .max(findLeadsResponseMaintenanceMaintenanceItemsItemSrtsItemCodeMax)
  .brand<'SrtCode'>();
export type SrtCode = z.infer<typeof SrtCodeBrand>;

export const Note = z
  .string()
  .nullable()
  .transform((val) => {
    if (typeof val === 'string') {
      const trimmed = val.trim();
      return trimmed === '' ? null : trimmed;
    }
    return val;
  })
  .pipe(z.string().min(1).max(createNoteBodyContentMax).nullable())
  .brand<'Note'>();

export type Note = z.infer<typeof Note>;

export const CustomerNameBrand = z
  .string()
  .min(findLeadsResponseCustomerNameMin)
  .max(findLeadsResponseCustomerNameMax)
  .brand<'CustomerName'>();
export type CustomerName = z.infer<typeof CustomerNameBrand>;

export const ContactNameBrand = z
  .string()
  .min(findLeadsResponseCustomerContactNameMin)
  .max(findLeadsResponseCustomerContactNameMax)
  .brand<'ContactName'>();
export type ContactName = z.infer<typeof ContactNameBrand>;

export const MileageValueBrand = z
  .number()
  .int()
  .gte(findLeadsResponseMaintenanceDueMileageValueMin)
  .lte(findLeadsResponseMaintenanceDueMileageValueMax)
  .brand<'MileageValue'>();
export type MileageValue = z.infer<typeof MileageValueBrand>;

export const QuantityValueBrand = z
  .number()
  .gte(
    findLeadsResponseMaintenanceMaintenanceItemsItemPartsItemQuantityValueMin,
  )
  .lte(
    findLeadsResponseMaintenanceMaintenanceItemsItemPartsItemQuantityValueMax,
  )
  .multipleOf(
    findLeadsResponseMaintenanceMaintenanceItemsItemPartsItemQuantityValueMultipleOf,
  )
  .brand<'QuantityValue'>();
export type QuantityValue = z.infer<typeof QuantityValueBrand>;

export const DescriptionBrand = z
  .string()
  .min(findLeadsResponseMaintenanceMaintenanceItemsItemDescriptionMin)
  .max(findLeadsResponseMaintenanceMaintenanceItemsItemDescriptionMax)
  .brand<'Description'>();
export type Description = z.infer<typeof DescriptionBrand>;

export const SrtHoursBrand = z
  .number()
  .gte(findLeadsResponseMaintenanceMaintenanceItemsItemSrtsItemHoursMin)
  .lte(findLeadsResponseMaintenanceMaintenanceItemsItemSrtsItemHoursMax)
  .multipleOf(
    findLeadsResponseMaintenanceMaintenanceItemsItemSrtsItemHoursMultipleOf,
  )
  .brand<'SrtHours'>();
export type SrtHours = z.infer<typeof SrtHoursBrand>;

export const LeadLifecycleStatus = {
  Lead: 'Lead',
  Scheduled: 'Scheduled',
  InRepair: 'In Repair',
  Executed: 'Executed',
  Archived: 'Archived',
} as const;

export type LeadLifecycleStatus =
  (typeof LeadLifecycleStatus)[keyof typeof LeadLifecycleStatus];

export const LeadUrgency = {
  Overdue: 'Overdue',
  OverdueSoon: 'Overdue Soon',
} as const;

export type LeadUrgency = (typeof LeadUrgency)[keyof typeof LeadUrgency];

export const LeadContactability = {
  Unreachable: 'Unreachable',
} as const;

export type LeadContactability =
  (typeof LeadContactability)[keyof typeof LeadContactability];
export const CustomerSchema = z.object({
  id: CustomerIdBrand,
  name: CustomerNameBrand,
  contactName: ContactNameBrand.nullable(),
  contactPhone: PhoneNumberBrand.nullable(),
  isUnknown: z.boolean().optional().default(false),
});
export type Customer = z.infer<typeof CustomerSchema>;

export const UnknownCustomer: Customer = CustomerSchema.parse({
  id: CustomerIdBrand.parse('CUST-UNKNOWN'),
  name: CustomerNameBrand.parse('Unknown Customer'),
  contactName: null,
  contactPhone: null,
  isUnknown: true,
});

export const LeadCreatedAtBrand = RFC3339InstantString.brand<'LeadCreatedAt'>();
export type LeadCreatedAt = z.infer<typeof LeadCreatedAtBrand>;

export const LeadNoteCreatedAtBrand =
  RFC3339InstantString.brand<'LeadNoteCreatedAt'>();
export type LeadNoteCreatedAt = z.infer<typeof LeadNoteCreatedAtBrand>;

export const LeadNoteCreatorSchema = z.object({
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  email: z.string(),
});
export type LeadNoteCreator = z.infer<typeof LeadNoteCreatorSchema>;

export const LeadNoteSchema = z.object({
  id: LeadNoteIdBrand,
  content: LeadNoteContentBrand,
  createdAt: LeadNoteCreatedAtBrand,
  createdBy: LeadNoteCreatorSchema.optional(),
});
export type LeadNote = z.infer<typeof LeadNoteSchema>;

export const InRepairAtBrand = RFC3339InstantString.brand<'InRepairAt'>();
export type InRepairAt = z.infer<typeof InRepairAtBrand>;

export const LeadScheduledDateBrand =
  RFC3339ZonedDateTimeString.brand<'LeadScheduledDate'>();
export type LeadScheduledDate = z.infer<typeof LeadScheduledDateBrand>;

export const CustomerUnreachableAtBrand =
  RFC3339InstantString.brand<'CustomerUnreachableAt'>();
export type CustomerUnreachableAt = z.infer<typeof CustomerUnreachableAtBrand>;

export const TechnicianAssignedAtBrand =
  RFC3339InstantString.brand<'TechnicianAssignedAt'>();
export type TechnicianAssignedAt = z.infer<typeof TechnicianAssignedAtBrand>;

export const PartsReadyAtBrand = RFC3339InstantString.brand<'PartsReadyAt'>();
export type PartsReadyAt = z.infer<typeof PartsReadyAtBrand>;

export const WorkStartedAtBrand = RFC3339InstantString.brand<'WorkStartedAt'>();
export type WorkStartedAt = z.infer<typeof WorkStartedAtBrand>;

export const WorkFinishedAtBrand =
  RFC3339InstantString.brand<'WorkFinishedAt'>();
export type WorkFinishedAt = z.infer<typeof WorkFinishedAtBrand>;

export const ClaimSubmittedAtBrand =
  RFC3339InstantString.brand<'ClaimSubmittedAt'>();
export type ClaimSubmittedAt = z.infer<typeof ClaimSubmittedAtBrand>;

export const ClaimApprovedAtBrand =
  RFC3339InstantString.brand<'ClaimApprovedAt'>();
export type ClaimApprovedAt = z.infer<typeof ClaimApprovedAtBrand>;

export const ArchivedAtBrand = RFC3339InstantString.brand<'ArchivedAt'>();
export type ArchivedAt = z.infer<typeof ArchivedAtBrand>;

export const NoShowAtBrand = RFC3339InstantString.brand<'NoShowAt'>();
export type NoShowAt = z.infer<typeof NoShowAtBrand>;

export interface LocalizableTemporal {
  toLocaleString(locale?: string, options?: Intl.DateTimeFormatOptions): string;

  toString(): string;
}

export type DomainTemporal = (
  | LeadCreatedAt
  | LeadNoteCreatedAt
  | InRepairAt
  | LeadScheduledDate
  | CustomerUnreachableAt
  | TechnicianAssignedAt
  | PartsReadyAt
  | WorkStartedAt
  | WorkFinishedAt
  | ClaimSubmittedAt
  | ClaimApprovedAt
  | ArchivedAt
  | NoShowAt
  | DueDate
) &
  LocalizableTemporal;

export const DueDateBrand = RFC3339PlainDateString.brand<'DueDate'>();
export type DueDate = z.infer<typeof DueDateBrand>;

export const WorkflowSchema = z.object({
  createdAt: LeadCreatedAtBrand,
  scheduledDate: LeadScheduledDateBrand.nullable(),
  customerUnreachableAt: CustomerUnreachableAtBrand.nullable(),
  technicianAssignedAt: TechnicianAssignedAtBrand.nullable(),
  partsReadyAt: PartsReadyAtBrand.nullable(),
  workStartedAt: WorkStartedAtBrand.nullable(),
  workFinishedAt: WorkFinishedAtBrand.nullable(),
  claimSubmittedAt: ClaimSubmittedAtBrand.nullable(),
  claimApprovedAt: ClaimApprovedAtBrand.nullable(),
  noShowAt: NoShowAtBrand.nullable(),
  archivedAt: ArchivedAtBrand.nullable(),
  daysUntilDue: z.number().int(),
  status: z.enum(LeadLifecycleStatus),
  urgency: z.enum(LeadUrgency).nullable(),
  contactability: z.enum(LeadContactability).nullable(),
  statusGroup: z.custom<LeadStatusGroup>(),
});

export type Workflow = z.infer<typeof WorkflowSchema>;

export const DealerSchema = z.object({
  id: DealerId,
  name: DealerNameBrand,
  groupName: z
    .string()
    .min(1)
    .max(findLeadsResponseHomeDealerGroupNameMax)
    .nullish(),
  city: z.string().min(1).max(findLeadsResponseHomeDealerCityMax),
  state: z.string().regex(findLeadsResponseHomeDealerStateRegExp),
  locationCode: z.string().regex(findLeadsResponseHomeDealerLocationCodeRegExp),
});
export type Dealer = z.infer<typeof DealerSchema>;

export const MileageSchema = z.object({
  value: MileageValueBrand,
  unit: z.enum(['mi', 'km']),
});
export type Mileage = z.infer<typeof MileageSchema>;

export const QuantitySchema = z.object({
  value: QuantityValueBrand,
  unit: z.enum([
    'Each',
    'Gallon',
    'Pint',
    'Liter',
    'Quart',
    'Pound',
    'Kilogram',
  ]),
});
export type Quantity = z.infer<typeof QuantitySchema>;

export const PartSchema = z
  .object({
    number: PartNumberBrand,
    quantity: QuantitySchema,
    description: DescriptionBrand,
  })
  .transform((part, _) => {
    return {
      ...part,
      get display(): string {
        return `${part.number} ${part.description}`;
      },
    };
  });
export type Part = z.output<typeof PartSchema>;

export const SrtSchema = z.object({
  code: SrtCodeBrand,
  description: DescriptionBrand,
  hours: SrtHoursBrand,
});
export type Srt = z.infer<typeof SrtSchema>;

export const MaintenanceItemSchema = z.object({
  vmrsCode: VmrsCodeBrand,
  description: DescriptionBrand,
  parts: z.array(PartSchema),
  srts: z.array(SrtSchema).min(1),
});
export type MaintenanceItem = z.infer<typeof MaintenanceItemSchema>;

export const MaintenanceSchema = z.object({
  dueDate: DueDateBrand,
  sellingDealerId: DealerSchema,
  dueMileage: MileageSchema.nullable(),
  maintenanceItems: z.array(MaintenanceItemSchema),
});
export type Maintenance = z.infer<typeof MaintenanceSchema>;

export const LeadSchema = z
  .object({
    id: LeadId,
    vin: VinBrand,
    homeDealer: DealerSchema.nullable(),
    odometer: MileageSchema.nullish(),
    customer: CustomerSchema.nullable().transform((c) => c ?? UnknownCustomer),
    workflow: WorkflowSchema.omit({
      daysUntilDue: true,
      status: true,
      urgency: true,
      contactability: true,
      statusGroup: true,
    }),
    maintenance: MaintenanceSchema,
  })
  .transform((leadArg, _) => {
    return {
      ...leadArg,
      get workflow(): Workflow {
        return extendWorkflow(leadArg.workflow, leadArg.maintenance.dueDate);
      },
    };
  });

export type Lead = z.output<typeof LeadSchema>;

export class Leads {
  private constructor(readonly values: readonly Lead[]) {}

  static from(leads: Lead[] | readonly Lead[]): Leads {
    return new Leads(Object.freeze([...leads]));
  }
}

export const WorkflowPatch = z.object({
  scheduledDate: LeadScheduledDateBrand.nullish(),
  customerUnreachableAt: CustomerUnreachableAtBrand.nullish(),
  technicianAssignedAt: TechnicianAssignedAtBrand.nullish(),
  partsReadyAt: PartsReadyAtBrand.nullish(),
  workStartedAt: WorkStartedAtBrand.nullish(),
  workFinishedAt: WorkFinishedAtBrand.nullish(),
  claimSubmittedAt: ClaimSubmittedAtBrand.nullish(),
  claimApprovedAt: ClaimApprovedAtBrand.nullish(),
  noShowAt: NoShowAtBrand.nullish(),
  archivedAt: ArchivedAtBrand.nullish(),
});

export type WorkflowPatch = z.output<typeof WorkflowPatch>;

export const LeadPatch = z.object({
  odometer: MileageSchema.nullish(),
});

export type LeadPatch = z.output<typeof LeadPatch>;
